import React, { useState, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlay, PiArrowUpRight, PiX, PiCamera } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import { getImageUrl } from "@/utils/image-url";
import logger from "@/utils/logger";

export default function UniversityHero({ content, onContentChange, isSelected }: SectionContentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const c = content || {};

  const leftInputRef = useRef<HTMLInputElement>(null);
  const rightInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (file: File, fieldKey: "imageUrl" | "imageUrl2") => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadPromise = homepageApi.uploadImage(file);
    toast.promise(uploadPromise, {
      loading: "Uploading image...",
      success: "Image uploaded successfully!",
      error: "Failed to upload image",
    });

    try {
      const response = await uploadPromise;
      if (response.success && response.data?.url) {
        onContentChange?.({
          ...c,
          [fieldKey]: response.data.url
        });
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (err) {
      logger.error(err);
      toast.error("Upload failed");
    }
  };

  function getEmbedUrl(url: string) {
    if (!url) return "";
    let videoId = "";
    if (url.includes("v=")) {
      videoId = url.split("v=")[1]?.split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("embed/")) {
      return url;
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
  }

  const tag = c.tag || "knowledge meets innovation";
  const title = c.title || "UNIVERSITY";
  const highlightText = c.highlightText || "OF INSTACKS";
  const subtitle = c.subtitle || "Remember to tailor the section names to fit the specific needs and structure of your university website.";
  const primaryCta = c.primaryCta || "View Our Program";
  const circleText = c.circleText || "* Explore Future *Instacks University * Estd. 1971 ";
  const rawImageUrl1 = c.imageUrl || "/images/university-student.png";
  const rawImageUrl2 = c.imageUrl2 || "https://images.unsplash.com/photo-1562774053-701939374585?w=400&h=400&fit=crop&auto=format";
  const imageUrl1 = getImageUrl(rawImageUrl1) || rawImageUrl1;
  const imageUrl2 = getImageUrl(rawImageUrl2) || rawImageUrl2;

  return (
    <div
      className={`relative overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        background: c.colorPalette?.primary || "#7A1520",
        fontFamily: "'Georgia', 'Times New Roman', serif",
        minHeight: "640px",
      }}
    >
      {/* Vertical grid lines overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px)",
          backgroundSize: "25% 100%",
        }}
      />

      {/* Diagonal stripe decoration (top-right corner) */}
      <div
        className="absolute top-0 right-0 pointer-events-none overflow-hidden"
        style={{ width: "300px", height: "300px" }}
      >
        <div
          className="absolute"
          style={{
            top: "-60px",
            right: "-60px",
            width: "350px",
            height: "350px",
            backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 2px, transparent 2px, transparent 16px)",
            transform: "rotate(0deg)",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-8 py-16 lg:py-20">
        <div className="relative flex items-center justify-between" style={{ minHeight: "480px" }}>
          {/* Left Image - flush/hanging */}
          <div
            onClick={(e) => {
              if (isSelected) {
                e.stopPropagation();
                leftInputRef.current?.click();
              }
            }}
            className={`hidden lg:block absolute ${isSelected ? "group/img cursor-pointer" : ""}`}
            style={{
              left: "-10px",
              top: "-40px",
              bottom: "-40px",
              width: "250px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              borderRadius: "2px",
              zIndex: 20,
              overflow: "hidden",
            }}
          >
            <img
              src={imageUrl1}
              alt="University Student"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = "/images/university-student.png"; }}
            />
            {isSelected && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs gap-1.5 font-sans font-medium">
                <PiCamera className="w-6 h-6" />
                <span>Upload Left Image</span>
              </div>
            )}
            <input
              type="file"
              ref={leftInputRef}
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file, "imageUrl");
              }}
            />
          </div>

          {/* Center Content */}
          <div className="w-full lg:pl-[280px] lg:pr-[240px] flex flex-col justify-center items-start pt-2">
            {/* Tagline */}
            <div className="flex items-center gap-3 mb-8">
              <svg width="32" height="23" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 text-white fill-current opacity-90">
                <path d="M29.3134 12.4137L26.2577 13.5368C26.1486 11.972 25.9624 10.4411 25.703 8.97501L31.2363 7.1272C31.7003 6.97226 32 6.55338 32 6.06003C32 5.56667 31.7003 5.14773 31.2363 4.99285L16.5518 0.0891783C16.1959 -0.0297261 15.8039 -0.0297261 15.4482 0.0891783L0.763689 4.99285C0.299751 5.14779 0 5.56667 0 6.06009C0 6.5535 0.299751 6.97238 0.763689 7.12726L6.29608 8.97476C6.15651 9.76449 6.03639 10.5862 5.93857 11.4221C5.90826 11.6818 6.09201 11.9171 6.34908 11.9478C6.36776 11.95 6.38633 11.951 6.4047 11.951C6.63901 11.951 6.84145 11.7739 6.86958 11.5332C6.9597 10.7628 7.06926 10.0054 7.19564 9.27518L8.6862 9.77295V10.9503C7.93627 11.1584 7.38358 11.8525 7.38358 12.6749C7.38358 13.1708 7.58452 13.6201 7.90833 13.9443C7.67095 14.8208 7.48783 15.8479 7.3597 17.0185C7.03995 16.8339 6.80164 16.6748 6.64233 16.5609C6.59533 16.5273 6.56758 16.4718 6.56801 16.4124C6.57483 15.4776 6.61164 14.5281 6.67751 13.5903C6.69583 13.3295 6.50133 13.1032 6.24308 13.0847C5.98545 13.0665 5.76064 13.2626 5.74232 13.5234C5.67514 14.481 5.63751 15.4507 5.63051 16.4053C5.62776 16.774 5.80357 17.121 6.10057 17.3334C6.33545 17.5014 6.72264 17.7565 7.26414 18.0453C7.18464 19.0703 7.1422 20.1902 7.1372 21.4014C7.1357 21.7627 7.37683 22.078 7.72377 22.1684C8.03083 22.2483 8.52845 22.3436 9.15496 22.3436C9.78139 22.3436 10.279 22.2483 10.5861 22.1683C10.933 22.078 11.1741 21.7626 11.1726 21.4014C11.17 20.7409 11.1559 20.1081 11.131 19.5025C11.7646 19.6636 12.4229 19.7997 13.0975 19.9085C13.1153 20.2805 13.195 20.6732 13.3337 21.0578C13.5537 21.6683 13.9002 22.1985 14.3094 22.5508C14.6521 22.8459 15.0262 23 15.3802 23C15.5033 23 15.6238 22.9803 15.7398 22.9424C15.7533 22.9388 15.7667 22.9346 15.78 22.9297L23.0251 20.2668C23.0252 20.2668 23.0252 20.2668 23.0252 20.2668L23.0266 20.2663C23.3112 20.162 23.5583 20.1628 23.7821 20.2687C24.2929 20.5104 24.5982 21.2385 24.7058 21.5405C24.7766 21.7388 24.9196 21.8972 25.1087 21.9867C25.2141 22.0365 25.3266 22.0616 25.4396 22.0616C25.5298 22.0616 25.6203 22.0456 25.7076 22.0135L27.8589 21.2228C28.0712 21.1447 28.2399 20.9778 28.3214 20.7647C28.4023 20.5532 28.389 20.3172 28.2849 20.1171C27.9832 19.5378 27.5121 19.1053 26.9035 18.8412L27.6523 18.566C27.8956 18.4766 28.021 18.2049 27.9324 17.9593C27.8439 17.7136 27.5749 17.5869 27.3316 17.6764L26.0489 18.1478C26.1166 17.647 26.0522 17.0589 25.8371 16.4621C25.6297 15.8867 25.3097 15.3827 24.9309 15.0316L29.6337 13.3032C29.9127 13.2004 30.5359 13.6587 30.8786 14.6092C31.0416 15.0617 31.0997 15.5341 31.0379 15.9054C30.9924 16.1781 30.8873 16.3696 30.7564 16.4177L29.2894 16.9568C29.0462 17.0462 28.9207 17.3178 29.0093 17.5635C29.0977 17.809 29.3664 17.9358 29.6101 17.8464L31.0771 17.3073C31.536 17.1386 31.8587 16.6849 31.9624 16.0625C32.0516 15.5269 31.9796 14.8958 31.7596 14.2854C31.2652 12.9136 30.1907 12.0913 29.3134 12.4137ZM9.15496 11.8329C9.61471 11.8329 9.98877 12.2106 9.98877 12.6749C9.98877 13.1392 9.61477 13.5169 9.15496 13.5169C8.69514 13.5169 8.32108 13.1392 8.32108 12.6749C8.32108 12.2106 8.69514 11.8329 9.15496 11.8329ZM9.15496 21.3969C8.68827 21.3969 8.30852 21.3328 8.07539 21.2802C8.09727 17.8463 8.43527 15.6933 8.75489 14.417C8.88358 14.4471 9.01739 14.4636 9.15496 14.4636C9.29258 14.4636 9.42633 14.4471 9.55502 14.417C9.87421 15.6917 10.2125 17.845 10.2345 21.2802C10.0013 21.3329 9.62152 21.3969 9.15496 21.3969ZM10.4015 13.9443C10.7253 13.6201 10.9262 13.1708 10.9262 12.6749C10.9262 11.8525 10.3735 11.1584 9.62364 10.9503V10.0861L15.448 12.0311C15.626 12.0905 15.8129 12.1202 15.9999 12.1202C16.1868 12.1202 16.3738 12.0905 16.5517 12.0311L20.3488 10.7631C20.5946 10.681 20.7279 10.4133 20.6467 10.165C20.5654 9.91685 20.3004 9.78197 20.0545 9.86421L16.2573 11.1322C16.0913 11.1877 15.9084 11.1877 15.7424 11.1322L10.2188 9.28755L14.5709 6.37931C14.5903 6.38941 14.6095 6.39964 14.6297 6.40917C14.9778 6.57351 15.4368 6.65688 15.9938 6.65688C16.5509 6.65688 17.0098 6.57357 17.358 6.40917C17.8436 6.17988 18.111 5.78807 18.111 5.30589C18.111 4.40237 17.181 3.69462 15.9938 3.69462C14.8067 3.69462 13.8767 4.40237 13.8767 5.30589C13.8767 5.43426 13.8963 5.55607 13.9333 5.67024L9.08602 8.90931L1.058 6.22847C0.953189 6.19345 0.937502 6.10793 0.937502 6.06009C0.937502 6.01225 0.953189 5.92673 1.058 5.8917L15.7425 0.987968C15.9085 0.932492 16.0914 0.932492 16.2574 0.987968L30.9419 5.8917C31.0468 5.92673 31.0624 6.01225 31.0624 6.06009C31.0624 6.10793 31.0468 6.19345 30.9419 6.22847L22.0294 9.20468C21.7835 9.28673 21.6502 9.55452 21.7314 9.80274C21.8127 10.0509 22.0779 10.1857 22.3237 10.1035L24.8044 9.27512C25.0574 10.7384 25.2417 12.3163 25.3413 13.8737C25.3413 13.8737 21.2618 15.3731 21.2614 15.3732C21.2613 15.3733 21.2612 15.3733 21.2611 15.3733L14.0162 18.0361C14.0155 18.0363 14.0148 18.0366 14.0142 18.0369C13.6373 18.1762 13.3528 18.5081 13.2062 18.9676C12.472 18.8478 11.7588 18.6946 11.0786 18.511C10.9613 16.7046 10.735 15.1757 10.4015 13.9443ZM14.8142 5.30589C14.8142 4.99216 15.3187 4.64131 15.9938 4.64131C16.669 4.64131 17.1735 4.99222 17.1735 5.30589C17.1735 5.58365 16.562 5.71019 15.9938 5.71019C15.4257 5.71019 14.8142 5.58365 14.8142 5.30589ZM22.1237 16.4724C22.4075 16.7167 22.6637 17.1164 22.8268 17.5688C22.9899 18.0213 23.048 18.4937 22.9861 18.865C22.9407 19.1376 22.8355 19.3291 22.7047 19.3772L16.679 21.5919C16.7457 21.0771 16.67 20.4833 16.4625 19.9079C16.2551 19.3325 15.9352 18.8285 15.5565 18.4774L21.5822 16.2627C21.7127 16.2147 21.9154 16.2931 22.1237 16.4724ZM14.2147 20.7341C14.0516 20.2817 13.9935 19.8092 14.0553 19.4379C14.1007 19.1653 14.2059 18.974 14.3367 18.9258C14.3367 18.9258 14.3368 18.9258 14.3368 18.9257L14.3371 18.9256C14.3624 18.9163 14.3903 18.9117 14.4205 18.9117C14.5463 18.9117 14.7105 18.9907 14.8784 19.1353C15.1622 19.3796 15.4184 19.7792 15.5815 20.2317C15.7445 20.6841 15.8027 21.1566 15.7408 21.5279C15.6953 21.8005 15.5902 21.992 15.4593 22.0401C15.3287 22.0882 15.126 22.0098 14.9177 21.8305C14.634 21.5862 14.3777 21.1866 14.2147 20.7341ZM27.3619 20.3982L25.532 21.0708C25.3689 20.657 25.0747 20.0814 24.6035 19.6868L25.1035 19.503C25.3586 19.4775 26.7069 19.3948 27.3619 20.3982ZM24.2532 15.6898C24.537 15.9341 24.7933 16.3337 24.9564 16.7862C25.2988 17.7364 25.113 18.4916 24.8345 18.5945L23.9242 18.929C23.9909 18.4142 23.9152 17.8205 23.7077 17.2451C23.5004 16.6697 23.1805 16.1656 22.8018 15.8145L23.7116 15.48C23.8426 15.4321 24.0449 15.5104 24.2532 15.6898Z" fill="white"/>
              </svg>
              <span
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: "'Inter', 'Helvetica', sans-serif",
                  fontSize: "14px",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                <EditableText
                  value={tag}
                  onChange={(val) => onContentChange?.({ ...c, tag: val })}
                  as="span"
                  placeholder="Tagline"
                />
              </span>
            </div>

            {/* Main Title - Line 1 */}
            <h1
              className="leading-none text-left"
              style={{
                fontSize: "clamp(56px, 8vw, 110px)",
                fontWeight: 400,
                color: "#FFFFFF",
                letterSpacing: "0.02em",
                lineHeight: 1,
                textTransform: "uppercase",
              }}
            >
              <EditableText
                value={title}
                onChange={(val) => onContentChange?.({ ...c, title: val })}
                as="span"
                placeholder="Title"
              />
            </h1>

            {/* Title Line 2 + Circle Badge on same row */}
            <div className="flex items-center gap-4 mt-1 relative">
              {/* Spinning Circle Badge */}
              <div
                className="relative flex-shrink-0 group/circle"
                style={{ width: "130px", height: "130px" }}
              >
                {/* Curly Arrow */}
                <div className="absolute -top-12 -left-6 pointer-events-none z-20">
                  <svg width="45" height="76" viewBox="0 0 45 76" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M38 3C30 18 10 28 12 43C14 58 35 55 33 42C30 30 15 32 18 47C21 62 30 67 42 64"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      fill="none"
                    />
                    <path
                      d="M35 68 L42 64 L37 57"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </div>
                <svg
                  viewBox="0 0 130 130"
                  className="w-full h-full"
                  style={{
                    animation: "uniHeroSpin 12s linear infinite",
                  }}
                >
                  <defs>
                    <path
                      id="uni-hero-circle-path"
                      d="M65,65 m-50,0a50,50 0 1,1 100,0a50,50 0 1,1 -100,0"
                      fill="none"
                    />
                  </defs>
                  <text
                    fill="rgba(255,255,255,0.6)"
                    fontSize="12"
                    fontFamily="'Inter', 'Helvetica', sans-serif"
                    letterSpacing="2"
                  >
                    <textPath xlinkHref="#uni-hero-circle-path">
                      {circleText}
                    </textPath>
                  </text>
                </svg>
                {/* Play button center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={() => setIsOpen(true)}
                    className="flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110"
                    style={{
                      width: "44px",
                      height: "44px",
                      background: "transparent",
                      border: "1.5px solid rgba(255,255,255,0.5)",
                    }}
                  >
                    <PiPlay className="w-5 h-5 text-white ml-0.5" />
                  </button>
                </div>

                {/* Edit overlay - appears on hover when section is selected */}
                {isSelected && (
                  <div
                    className="absolute -bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover/circle:opacity-100 transition-opacity z-20"
                    style={{ width: "220px" }}
                  >
                    <input
                      type="text"
                      value={circleText}
                      onChange={(e) => onContentChange?.({ ...c, circleText: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-md text-xs text-gray-800 bg-white shadow-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Edit ring text..."
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>

              {/* "OF INSTACKS" */}
              <span
                style={{
                  fontSize: "clamp(56px, 8vw, 110px)",
                  fontWeight: 400,
                  color: "#FFFFFF",
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                  textTransform: "uppercase",
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                }}
              >
                <EditableText
                  value={highlightText}
                  onChange={(val) => onContentChange?.({ ...c, highlightText: val })}
                  as="span"
                  placeholder="Subtitle"
                />
              </span>
            </div>

            {/* Description */}
            <p
              className="mt-8 max-w-lg text-left"
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "15px",
                lineHeight: 1.75,
                fontFamily: "'Inter', 'Helvetica', sans-serif",
              }}
            >
              <EditableText
                value={subtitle}
                onChange={(val) => onContentChange?.({ ...c, subtitle: val })}
                as="span"
                placeholder="Description"
              />
            </p>

            {/* CTA Button */}
            <a
              href={c.primaryCtaLink || "#"}
              className="inline-flex items-center gap-3 group transition-all mt-8 hover:bg-white/10"
              style={{
                color: "#FFFFFF",
                fontSize: "15px",
                fontFamily: "'Inter', 'Helvetica', sans-serif",
                fontWeight: 400,
                padding: "14px 32px",
                border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: "0px",
                letterSpacing: "0.03em",
              }}
            >
              <EditableText
                value={primaryCta}
                onChange={(val) => onContentChange?.({ ...c, primaryCta: val })}
                as="span"
                placeholder="Button text"
              />
              <PiArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>

          {/* Right Image - hanging from top right */}
          <div
            onClick={(e) => {
              if (isSelected) {
                e.stopPropagation();
                rightInputRef.current?.click();
              }
            }}
            className={`hidden lg:block absolute ${isSelected ? "group/img cursor-pointer" : ""}`}
            style={{
              right: "-10px",
              top: "-40px",
              width: "200px",
              height: "260px",
              borderRadius: "2px",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              zIndex: 20,
            }}
          >
            <img
              src={imageUrl2}
              alt="University Campus"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = "/images/default-training.png"; }}
            />
            {isSelected && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-xs gap-1.5 font-sans font-medium">
                <PiCamera className="w-6 h-6" />
                <span>Upload Right Image</span>
              </div>
            )}
            <input
              type="file"
              ref={rightInputRef}
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file, "imageUrl2");
              }}
            />
          </div>
        </div>
      </div>

      {/* Video Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-4xl aspect-video rounded-lg overflow-hidden bg-black shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 z-[10000] p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors focus:outline-none"
            >
              <PiX className="w-6 h-6" />
            </button>
            <iframe
              src={getEmbedUrl(c.videoUrl || "https://www.youtube.com/watch?v=7ahgosTZJHg")}
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="University Introduction Video"
            />
          </div>
        </div>
      )}

      {/* Keyframe for spinning circle */}
      <style>{`
        @keyframes uniHeroSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
